import { isEmpty } from 'common/utils/vanilla'

export type APIReferralError = {
  errors: Array<{ loc: string[]; msg: string; type: string }>
  code: string
  service: string
  track_id: string
  aditional_info?: Record<string, unknown>
  environment: string
}

export const parseReferralAPIError = (
  error: APIReferralError
): Record<string, string> => {
  if (!isEmpty(error.errors)) {
    return error.errors
      .map(e => {
        // For nested paths (e.g., ["body", "labra_referral", "Labra__Description__c"]),
        // the actual field name is at the last index. For simple paths (e.g., ["Labra__Description__c"]),
        // the field name is at index 0.
        const fieldName = e.loc.length > 0 ? e.loc[e.loc.length - 1] : ''
        return { key: fieldName, msg: e.msg }
      })
      .reduce((acc, e) => ({ ...acc, [e.key]: e.msg }), {})
  }
  return {}
}

/**
 * Extracts error messages from parsed errors to display in alerts.
 * Combines multiple error messages into a single user-friendly string.
 */
export const getErrorMessagesFromParsedErrors = (
  parsedErrors: Record<string, string>
): string[] => {
  return Object.values(parsedErrors).filter(msg => msg && msg.trim().length > 0)
}

/**
 * Filters errors to only include those for fields that don't exist in the form.
 * Optimized version using Set for O(1) lookups.
 * @param parsedErrors - The parsed error object with field keys and error messages
 * @param existingFieldIds - Array of field IDs that exist in the form
 * @returns Filtered errors object containing only errors for fields not in the form
 */
export const filterErrorsForNonExistentFields = (
  parsedErrors: Record<string, string>,
  existingFieldIds: string[]
): Record<string, string> => {
  const existingFieldsSet = new Set(existingFieldIds)
  const filtered: Record<string, string> = {}

  for (const [fieldKey, errorMessage] of Object.entries(parsedErrors)) {
    if (!existingFieldsSet.has(fieldKey)) {
      filtered[fieldKey] = errorMessage
    }
  }

  return filtered
}

/**
 * Optimized helper to build a cache of all field names in a form structure.
 * Creates a Set for O(1) field existence checks.
 */
const buildFormFieldCache = (
  formObject: Record<string, object>
): Set<string> => {
  const fieldSet = new Set<string>()

  // Iterate through all sections and collect all field names
  for (const section of Object.values(formObject)) {
    if (section && typeof section === 'object') {
      for (const fieldName of Object.keys(section)) {
        fieldSet.add(fieldName)
      }
    }
  }

  return fieldSet
}

/**
 * Filters errors for fields that don't exist in a formik form structure.
 * Optimized for CoSell forms where errors are mapped from API field names to form field names.
 *
 * Performance optimizations:
 * - Builds a field cache Set for O(1) lookups instead of iterating through form each time
 * - Creates a reverse mapping upfront to avoid nested loops
 * - Uses Set operations for fast membership checks
 *
 * @param parsedErrors - Original parsed errors with API field names as keys
 * @param parsedErrorsTransformed - Transformed errors with form field names as keys (same values as parsedErrors)
 * @param formObject - The formik form structure (sections containing fields)
 * @returns Filtered errors object with only errors for fields not in the form
 */
export const filterErrorsForNonExistentFormikFields = (
  parsedErrors: Record<string, string>,
  parsedErrorsTransformed: Record<string, unknown>,
  formObject: Record<string, object> | undefined | null
): Record<string, string> => {
  // If form structure not available, return all errors to be safe
  if (!formObject) {
    return { ...parsedErrors }
  }

  // Build a cache of all fields that exist in the form for O(1) lookups
  const formFieldsCache = buildFormFieldCache(formObject)

  // Create a reverse mapping: mapped field name -> API field name
  // Since parsedErrors and parsedErrorsTransformed have the same values (error messages),
  // we can match them by error message to find the mapping
  const mappedFieldToApiField = new Map<string, string>()
  const apiFieldToMappedField = new Map<string, string>()

  // Build bidirectional mapping by matching error messages
  for (const [apiFieldKey, errorMessage] of Object.entries(parsedErrors)) {
    for (const [mappedFieldKey, mappedErrorMessage] of Object.entries(
      parsedErrorsTransformed
    )) {
      // Compare as strings to handle type differences
      if (String(mappedErrorMessage) === String(errorMessage)) {
        mappedFieldToApiField.set(mappedFieldKey, apiFieldKey)
        apiFieldToMappedField.set(apiFieldKey, mappedFieldKey)
        break // Found the mapping, move to next API field
      }
    }
  }

  // Collect errors for fields that don't exist in the form
  const errorsNotInForm: Record<string, string> = {}
  for (const [apiFieldKey, errorMessage] of Object.entries(parsedErrors)) {
    const mappedFieldKey = apiFieldToMappedField.get(apiFieldKey)

    // Check if the mapped field exists in the form using the cache
    const fieldExists = mappedFieldKey
      ? formFieldsCache.has(mappedFieldKey)
      : false

    if (!fieldExists) {
      // This field doesn't exist in the form, add its error to the alert
      errorsNotInForm[apiFieldKey] = errorMessage
    }
  }

  return errorsNotInForm
}

/**
 * Formats error messages for display in alerts.
 * Returns a formatted string with all error messages, or null if no errors.
 */
export const formatErrorMessagesForAlert = (
  parsedErrors: Record<string, string>
): string | null => {
  const messages = getErrorMessagesFromParsedErrors(parsedErrors)
  if (messages.length === 0) {
    return null
  }

  if (messages.length === 1) {
    return messages[0]
  }

  // For multiple errors, combine them in a list format
  return messages.map((msg, index) => `${index + 1}. ${msg}`).join('\n')
}
